import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import PageLayout from "../../common/PageLayout";
import PageTitle from "../../common/PageTitle";
import LinkButton from "../../core/LinkButton";
import useSearchListingsPage from "../../SearchListings/useSearchListingsPage";
import { Sidebar } from "./Sidebar";
import Map from "./Map";
import List from "./List";
import {
  Search as SearchIcon,
  FilterIcon,
  Printer,
  Download,
  Copy,
  HistoryIcon,
  Mail,
} from "lucide-react";
import { NeighborhoodSearchIcon } from "src/components/icons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../shadcn/Tabs";
import { useEffect, useMemo, useState, useRef } from "react";
import VerticalDivider from "../../common/VerticalDivider";
import { CombineListsDialog } from "./CombinedListsDialog";
import { CloneListDialog } from "./CloneListDialog";
import { PrintExportDialog } from "src/components/SearchListings/PrintExportDialog";
import { useListingDetailsQuery } from "../../../hooks/useListing";
import { useSavedSearches } from "../hooks/useSavedSearches";
import {
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
} from "../hooks/useFavoriteMutation";
import ManageNotes from "../../common/ManageNotes";
import ManageTags from "../../common/ManageTags";
import { EnumProductTypes, Listing } from "../../../types/listing";
import { GeoLocation } from "../../../types/common";
import Button from "src/components/core/Button";
import FetchingSendCardInfoDialog from "src/components/sendCards/FetchingSendCardInfoDialog";
import Badge from "src/components/core/Badge";
import Layout, { useLayout } from "../../common/page/Layout";
import { usePanelStack } from "../../common/page/usePanelStack";
import { SavedSearchesSelector } from "../search/SavedSearchesSelector";
import { useMultiSelectCombobox } from "src/components/core/MultiSelectComboBox";
import { ComboBoxListOption } from "src/components/core/ComboBox";
import { useCallback } from "react";
import { EnumSidebars } from "./Sidebar";
import { usePropertyFilters } from "../search/usePropertyFilters";
import { ActionsMenu, EnumActionType, MenuItem } from "./ActionsMenu";
import { MergeIcon } from "lucide-react";
import { useSearchListingsNS } from "../hooks/useSearchListingsNS";
import { OWNER_OCCUPANCY_FILTER, OwnerOccupancyFilter } from "../constants";
import { SEARCH_LISTING_QUERY_KEY } from "src/constants/cache";
import { OutputType } from "src/components/SearchListings/PrintExportDialog";
import { PrintType } from "src/components/SearchListings/useSearchListingsPage";
import { isNullOrUndefined } from "src/utils/objects";
import { isRegionSearch } from "../utils/isRegionSearch";
import { useSendCards, useSendCardsAccess } from "src/components/sendCards";
import { MAX_SEND_CARDS_LIMIT } from "src/components/sendCards/useSendCards";
import { useSyncToPartnersMenu } from "src/hooks/useSyncToPartnersMenu";
import { ConfirmDialog } from "src/components/core/ConfirmDialog";
import { CreditsBalance } from "../common/CreditsBalance";
import LookupStatusIndicator from "../../common/LookupStatusIndicator";
import { useFetchSavedSearchStatus } from "../hooks/useFetchSavedSearchStatus";
import {
  getLookupStatusFromCode,
  getLookupStatusLabel,
} from "../utils/savedSearchStatusHelpers";
import { SavedSearchLookupStatusCode } from "../constants";
import { useSavedSearchToasts } from "../hooks/useSavedSearchToasts";
import { useExportListingsQuery } from "../hooks/useExportListingsQuery";
import useAccount from "src/hooks/useAccount";
import { formatNumber } from "src/utils/numbers";
import { SelectedListingsType } from "src/components/SearchListings/useSearchListingsPage";

enum EnumPageTabs {
  Map = "map",
  List = "list",
}

const PAGE_TITLE_HEIGHT = 50;

const SavedSearch = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const { data: accountData } = useAccount();
  const panelStack = usePanelStack<EnumSidebars>(
    tab === EnumPageTabs.Map ? [EnumSidebars.PropertiesList] : []
  );
  const {
    open: openPanel,
    close: closePanel,
    closeTop: closeTopPanel,
  } = panelStack;
  const activeSidebar: EnumSidebars | null = panelStack.top;
  const panelCount = panelStack.stack.length;
  const [isCombinedListsDialogOpen, setIsCombinedListsDialogOpen] =
    useState(false);
  const [isCloneListDialogOpen, setIsCloneListDialogOpen] = useState(false);

  const { mutateAsync: addFavorite } = useAddFavoriteMutation();
  const { mutateAsync: removeFavorite } = useRemoveFavoriteMutation();
  const [selectedListingId, setSelectedListingId] = useState<
    string | undefined | null
  >(undefined);

  const [activePageTab, setActivePageTab] = useState<EnumPageTabs>(
    tab === EnumPageTabs.Map ? EnumPageTabs.Map : EnumPageTabs.List
  );

  const { data: selectedListing, isLoading: isLoadingSelectedListing } =
    useListingDetailsQuery(EnumProductTypes.ns, selectedListingId as string);

  const { isSidebarOpen, toggleSidebar } = useLayout();
  const { sendCards } = useSendCards();
  const hasSendCardsAccess = useSendCardsAccess();
  const [sendCardsProgress, setSendCardsProgress] = useState<{ completed: number; total: number } | null>(null);
  const sendCardsCancelledRef = useRef(false);

  const cancelSendCards = useCallback(() => {
    sendCardsCancelledRef.current = true;
    setSendCardsProgress(null);
  }, []);

  const [mapCenter, setMapCenter] = useState<GeoLocation | undefined>(
    undefined
  );
  const hasSetDefaultListingRef = useRef(false);
  const lastKnownTotalRecords = useRef<number>(0);
  const [isDownloadingListings, setIsDownloadingListings] = useState(false);

  const [searchParams] = useSearchParams();

  const initialSelectedIds = useMemo(
    () => searchParams.get("selected")?.split(",") || [],
    [searchParams]
  );

  const {
    selectedItems: selectedSavedSearchesOptions,
    handleSelect: handleSavedSearchSelect,
    handleRemove: handleSavedSearchRemove,
    setSelectedItems: setSelectedSavedSearchesOptions,
  } = useMultiSelectCombobox();

  // Set initial selection from URL params
  useEffect(() => {
    if (initialSelectedIds.length > 0) {
      setSelectedSavedSearchesOptions(
        initialSelectedIds.map(
          (id) =>
            ({
              label: id,
              value: id,
            } as ComboBoxListOption)
        )
      );
    }
  }, [initialSelectedIds, setSelectedSavedSearchesOptions]);

  const selectedIds = useMemo(
    () => selectedSavedSearchesOptions.map((item) => item.value),
    [selectedSavedSearchesOptions]
  );
  const { data: savedSearches } = useSavedSearches(selectedIds);

  // Only fetch status when there's exactly one search selected
  const singleSearchId = useMemo(() => {
    return selectedIds.length === 1 ? selectedIds[0] : null;
  }, [selectedIds]);

  const selectedSearchName = useMemo(() => {
    if (singleSearchId && savedSearches?.length) {
      const search = savedSearches.find((s) => s.id === singleSearchId);
      return search?.name || "your search";
    }
    return "your search";
  }, [singleSearchId, savedSearches]);

  const { data: statusData } = useFetchSavedSearchStatus(
    singleSearchId || "",
    !!singleSearchId
  );

  // Use the toast hook for saved search status notifications
  useSavedSearchToasts({
    lookupStatus: statusData?.lookup_status,
    searchId: singleSearchId,
    searchName: selectedSearchName,
  });

  // Get initial filter values from URL parameters
  const initialFilterValues = useMemo(() => {
    const propertyType = searchParams.get("propertyType");
    const bedrooms = searchParams.get("bedrooms");
    const bathrooms = searchParams.get("bathrooms");
    const squareFootage = searchParams.get("squareFootage");
    const acreage = searchParams.get("acreage");
    const yearBuilt = searchParams.get("yearBuilt");
    const favorite = searchParams.get("favorite");
    const companyOwned = searchParams.get("companyOwned");
    const hideDNCPhoneNumbers = searchParams.get("hideDNCPhoneNumbers");
    const ownerOccupied = searchParams.get("ownerOccupied");
    const tag = searchParams.get("tag");
    const note = searchParams.get("note");

    return {
      initialSelectedPropertyTypes: propertyType
        ? propertyType.split(",")
        : undefined,
      initialBeds: bedrooms
        ? [
            Number(bedrooms.split(",")[0]) || undefined,
            Number(bedrooms.split(",")[1]) || undefined,
          ]
        : undefined,
      initialBaths: bathrooms
        ? [
            Number(bathrooms.split(",")[0]) || undefined,
            Number(bathrooms.split(",")[1]) || undefined,
          ]
        : undefined,
      initialSqft: squareFootage
        ? [
            Number(squareFootage.split(",")[0]) || undefined,
            Number(squareFootage.split(",")[1]) || undefined,
          ]
        : undefined,
      initialAcreage: acreage
        ? [
            Number(acreage.split(",")[0]) || undefined,
            Number(acreage.split(",")[1]) || undefined,
          ]
        : undefined,
      initialYearBuilt: yearBuilt
        ? [
            Number(yearBuilt.split(",")[0]) || undefined,
            Number(yearBuilt.split(",")[1]) || undefined,
          ]
        : undefined,
      initialIsFavorite:
        favorite === "true" ? true : favorite === "false" ? false : undefined,
      initialIsShowOnlyPrivatelyOwned:
        companyOwned === "true"
          ? true
          : companyOwned === "false"
          ? false
          : undefined,
      initialIsHideDNCPhoneNumbers:
        hideDNCPhoneNumbers !== null
          ? hideDNCPhoneNumbers === "true"
          : accountData?.is_hide_dnc_phone_numbers || false,
      initialOwnerOccupied:
        (ownerOccupied as OwnerOccupancyFilter) || OWNER_OCCUPANCY_FILTER.ALL,
      initialSearchByTags: tag || "",
      initialSearchByNotes: note || "",
    };
  }, [searchParams, accountData]);

  // Initialize property filters with initial values from URL
  const {
    selectedPropertyTypes,
    setSelectedPropertyTypes,
    beds,
    setBeds,
    baths,
    setBaths,
    sqft,
    setSqft,
    acreage,
    setAcreage,
    yearBuilt,
    setYearBuilt,
    isFavorite,
    setIsFavorite,
    isShowOnlyPrivatelyOwned,
    setIsShowOnlyPrivatelyOwned,
    isHideDNCPhoneNumbers,
    setIsHideDNCPhoneNumbers,
    ownerOccupied,
    setOwnerOccupied,
    searchByTags,
    setSearchByTags,
    searchByNotes,
    setSearchByNotes,
    resetToDefaults,
  } = usePropertyFilters(initialFilterValues);

  // Apply filters from saved search when a single search is loaded
  useEffect(() => {
    if (singleSearchId && savedSearches?.length === 1) {
      const savedSearch = savedSearches[0];
      const searchFilters = savedSearch.search_parameters?.searchFilters;

      if (searchFilters) {
        // Apply property type filter
        if (searchFilters.propertyType) {
          // Convert comma-separated string to array
          const propertyTypes = searchFilters.propertyType.split(',').filter(Boolean);
          setSelectedPropertyTypes(propertyTypes);
        }

        // Apply bedroom filter
        if (searchFilters.bedrooms) {
          setBeds([
            searchFilters.bedrooms.min || undefined,
            searchFilters.bedrooms.max || undefined
          ]);
        }

        // Apply bathroom filter
        if (searchFilters.bathrooms) {
          setBaths([
            searchFilters.bathrooms.min || undefined,
            searchFilters.bathrooms.max || undefined
          ]);
        }

        // Apply square footage filter
        if (searchFilters.squareFootage) {
          setSqft([
            searchFilters.squareFootage.min || undefined,
            searchFilters.squareFootage.max || undefined
          ]);
        }

        // Apply acreage filter
        if (searchFilters.acreage) {
          setAcreage([
            searchFilters.acreage.min || undefined,
            searchFilters.acreage.max || undefined
          ]);
        }

        // Apply year built filter
        if (searchFilters.yearBuilt) {
          setYearBuilt([
            searchFilters.yearBuilt.min || undefined,
            searchFilters.yearBuilt.max || undefined
          ]);
        }
      }
    }
  }, [singleSearchId, savedSearches, setSelectedPropertyTypes, setBeds, setBaths, setSqft, setAcreage, setYearBuilt]);

  const searchListings = useSearchListingsNS({
    selectedIds,
    beds: isNullOrUndefined(beds)
      ? undefined
      : [beds[0] ?? null, beds[1] ?? null],
    baths: isNullOrUndefined(baths)
      ? undefined
      : [baths[0] ?? null, baths[1] ?? null],
    acreage: isNullOrUndefined(acreage)
      ? undefined
      : [acreage[0] ?? null, acreage[1] ?? null],
    sqft: isNullOrUndefined(sqft)
      ? undefined
      : [sqft[0] ?? null, sqft[1] ?? null],
    yearBuilt: isNullOrUndefined(yearBuilt)
      ? undefined
      : [yearBuilt[0] ?? null, yearBuilt[1] ?? null],
    selectedPropertyTypes,
    isShowOnlyPrivatelyOwned,
    isFavorite,
    isHideDNCPhoneNumbers,
    ownerOccupied,
    searchByTags:
      typeof searchByTags === "string"
        ? searchByTags.split(",").filter(Boolean)
        : [],
    searchByNotes:
      typeof searchByNotes === "string"
        ? searchByNotes.split(",").filter(Boolean)
        : [],
    setBeds,
    setBaths,
    setAcreage,
    setSqft,
    setYearBuilt,
    setSelectedPropertyTypes,
    setIsFavorite,
    setIsShowOnlyPrivatelyOwned,
    setIsHideDNCPhoneNumbers,
    setOwnerOccupied,
    setSearchByTags,
    setSearchByNotes,
  });

  // Set up export query for CSV downloads with raw lead data - using same parameters as useSearchListingsNS
  const exportQuery = useExportListingsQuery({
    selectedIds,
    beds: isNullOrUndefined(beds)
      ? undefined
      : [beds[0] ?? null, beds[1] ?? null],
    baths: isNullOrUndefined(baths)
      ? undefined
      : [baths[0] ?? null, baths[1] ?? null],
    acreage: isNullOrUndefined(acreage)
      ? undefined
      : [acreage[0] ?? null, acreage[1] ?? null],
    sqft: isNullOrUndefined(sqft)
      ? undefined
      : [sqft[0] ?? null, sqft[1] ?? null],
    yearBuilt: isNullOrUndefined(yearBuilt)
      ? undefined
      : [yearBuilt[0] ?? null, yearBuilt[1] ?? null],
    selectedPropertyTypes,
    isShowOnlyPrivatelyOwned,
    isFavorite,
    isHideDNCPhoneNumbers,
    searchByTags:
      typeof searchByTags === "string"
        ? searchByTags.split(",").filter(Boolean)
        : [],
    searchByNotes:
      typeof searchByNotes === "string"
        ? searchByNotes.split(",").filter(Boolean)
        : [],
  });

  const {
    pageSize,
    currentPage,
    infiniteSearchListings,
    infiniteSearchListingsIsLoading,
    infiniteSearchListingsFetchNextPage,
    infiniteSearchListingsHasNextPage,
    pagedSearchListings,
    pagedSearchListingsTotalRecords,
    pagedSearchListingsIsLoading,
    allSearchListings,
    updateSearchParams,
    activeFilters,
    allListingsQuery,
    fetchListingsInChunks: fetchListingsInChunksNS,
    searchCriteriaKey,
  } = searchListings;

  const {
    handleDeleteNoteClick,
    handleTagsSaveClick,
    handleAddNoteClick,
    manageNotesListing,
    manageTagsListing,
    setManageNotesListing,
    setManageTagsListing,
    printType,
    setPrintType,
    printListings,
    exportListingsToPdf,
    isBatchSelectionActive,
    toggleItemIsSelected,
    selectedListings,
    printContentRef,
    printAveryLabels,
    handlePrintSingleListingClick,
    isPrintingSingleListing,
    isPrintExportDialogOpen,
    outputType,
    setOutputType,
    handlePrintExportDialogOpenChange,
    toggleIsBatchSelectionActive,
    selectedItems,
    isSelectAllChecked,
    toggleSelectAll,
    selectedListingsType,
    setSelectedListingsType,
    selectedExportTemplate,
    setSelectedExportTemplate,
    exportTemplateOptions,
    getListingsForSendCards,
    getSendCardsCount,
    isFetchingAllListings: isFetchingListingsForPrint,
  } = useSearchListingsPage({
    totalRecords: pagedSearchListingsTotalRecords || 0,
    listings: pagedSearchListings || [],
    csvApiUrl: "",
    allListingsQuery,
    fetchListingsInChunks: (onProgress, cancelled) =>
      fetchListingsInChunksNS(onProgress, cancelled, pagedSearchListingsTotalRecords || 0),
    sortField: "",
    sortDir: "",
    searchQueryKey: SEARCH_LISTING_QUERY_KEY,
    exportedCsvFileName: "neighborhood-searches",
    productType: EnumProductTypes.ns,
    searchCriteriaKey,
  });


  // Custom downloadListings function that uses exportQuery for raw lead data
  const downloadListings = useCallback(async () => {
    if (!exportQuery || isDownloadingListings || exportQuery.isFetching) {
      return;
    }

    try {
      setIsDownloadingListings(true);

      // Fetch raw lead data from exportQuery
      const result = await exportQuery.refetch();
      let leadsToExport: any[] = [];

      if (result.data?.leads) {
        if (selectedListingsType === SelectedListingsType.selections && selectedItems.size > 0) {
          // Filter the fetched leads based on selected listing IDs
          leadsToExport = (result.data.leads as any[]).filter((lead) =>
            selectedItems.has(lead.id)
          );
        } else {
          // Use all leads
          leadsToExport = result.data.leads as any[];
        }
      }

      if (leadsToExport.length > 0) {
        // Get the first saved search name for the filename
        const firstSearchName =
          selectedSavedSearchesOptions[0]?.label || "neighborhood_search";
        const sanitizedName = firstSearchName
          .replace(/[^a-z0-9]/gi, "_")
          .toLowerCase();

        // Apply the selected export template to get the filtered columns
        const { applyExportTemplate } = await import(
          "../utils/exportLeadsToCSV"
        );
        const columns = applyExportTemplate(selectedExportTemplate);

        // Export leads using the exportLeadsToCSV utility with filtered columns
        const { exportLeadsToCSV } = await import("../utils/exportLeadsToCSV");
        exportLeadsToCSV(sanitizedName, leadsToExport, {
          columns,
          isHideDNCPhoneNumbers: isHideDNCPhoneNumbers ?? undefined,
        });
      }
    } catch (error) {
      console.error("Error downloading listings:", error);
    } finally {
      setIsDownloadingListings(false);
    }
  }, [
    exportQuery,
    selectedListingsType,
    selectedItems,
    isHideDNCPhoneNumbers,
    selectedSavedSearchesOptions,
    selectedExportTemplate,
    setIsDownloadingListings,
    isDownloadingListings,
  ]);

  // Calculate the display count for actions menu and dialogs
  const actionCount = useMemo(() => {
    // When batch mode is off, always show total records (all records mode)
    // When batch mode is on, show selected count if items are selected, otherwise show total
    return isBatchSelectionActive
      ? selectedItems.size > 0
        ? selectedItems.size
        : pagedSearchListingsTotalRecords || 0
      : pagedSearchListingsTotalRecords || 0;
  }, [
    isBatchSelectionActive,
    selectedItems.size,
    pagedSearchListingsTotalRecords,
  ]);

  const handleSendCardAction = useCallback(() => {
    const count = getSendCardsCount();
    if (count > MAX_SEND_CARDS_LIMIT) {
      sendCards([], "Neighborhood Search", count);
      return;
    }
    sendCardsCancelledRef.current = false;
    setSendCardsProgress({ completed: 0, total: 1 });
    getListingsForSendCards(
      (completed, total) => setSendCardsProgress({ completed, total }),
      sendCardsCancelledRef,
    ).then((listingsForCards) => {
      if (!sendCardsCancelledRef.current && listingsForCards.length > 0) {
        sendCards(listingsForCards, "Neighborhood Search");
      }
    }).finally(() => setSendCardsProgress(null));
  }, [getSendCardsCount, sendCards, getListingsForSendCards]);

  // Partner sync hook - returns ready-to-use menu items and dialog props
  const {
    showPartnerSync,
    partnerSyncMenuItems,
    partnerSyncDialogProps,
  } = useSyncToPartnersMenu({
    productType: "ns",
    actionCount,
    getRecordIds: async () => {
      if (isBatchSelectionActive && selectedItems.size > 0) {
        return Array.from(selectedItems);
      }
      const allListings = await getListingsForSendCards();
      return allListings.map((l: Listing) => l.id);
    },
  });

  // Define actions menu items for neighborhood search
  const neighborhoodMenuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [
      {
        type: EnumActionType.Print,
        icon: <Printer className="h-4 w-4" />,
        label: `Print (${formatNumber(actionCount, "0")})`,
        onAction: () => {
          setOutputType(OutputType.print);
          handlePrintExportDialogOpenChange(true);
        },
      },
      {
        type: EnumActionType.Export,
        icon: <Download className="h-4 w-4" />,
        label: `Export (${formatNumber(actionCount, "0")})`,
        onAction: () => {
          setOutputType(OutputType.export);
          handlePrintExportDialogOpenChange(true);
        },
      },
    ];

    if (hasSendCardsAccess) {
      items.push({
        type: EnumActionType.SendCard,
        icon: <Mail className="h-4 w-4" />,
        label: `Send Card (${formatNumber(actionCount, "0")})`,
        disabled: actionCount === 0,
        onAction: handleSendCardAction,
      });
    }

    // Add partner sync items (from hook)
    if (showPartnerSync) {
      items.push(...partnerSyncMenuItems);
    }

    items.push(
      {
        type: EnumActionType.CombineLists,
        icon: <MergeIcon className="h-4 w-4" />,
        label: "Combine Lists",
        onAction: () => setIsCombinedListsDialogOpen(true),
      },
      {
        type: EnumActionType.CloneList,
        icon: <Copy className="h-4 w-4" />,
        label: "Re-run Search",
        onAction: () => setIsCloneListDialogOpen(true),
      }
    );

    return items;
  }, [actionCount, hasSendCardsAccess, handleSendCardAction, setOutputType, handlePrintExportDialogOpenChange, showPartnerSync, partnerSyncMenuItems]);

  // Auto-switch to selections mode when items are selected
  useEffect(() => {
    if (selectedItems.size > 0) {
      setSelectedListingsType(SelectedListingsType.selections);
    }
  }, [selectedItems.size, setSelectedListingsType]);

  // Handle URL synchronization for user-initiated filter changes
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // Skip the first render to avoid syncing initial values back to URL
    if (!hasInitialized) {
      setHasInitialized(true);
      return;
    }

    // Sync filter changes to URL (only after initial load)
    const newSearchParams = new URLSearchParams(searchParams);

    // Update search params based on current filter values
    if (selectedPropertyTypes.length > 0) {
      newSearchParams.set("propertyType", selectedPropertyTypes.join(","));
    } else {
      newSearchParams.delete("propertyType");
    }

    if (
      !isNullOrUndefined(beds) &&
      (beds[0] !== undefined || beds[1] !== undefined)
    ) {
      newSearchParams.set("bedrooms", `${beds[0] || ""},${beds[1] || ""}`);
    } else {
      newSearchParams.delete("bedrooms");
    }

    if (
      !isNullOrUndefined(baths) &&
      (baths[0] !== undefined || baths[1] !== undefined)
    ) {
      newSearchParams.set("bathrooms", `${baths[0] || ""},${baths[1] || ""}`);
    } else {
      newSearchParams.delete("bathrooms");
    }

    if (sqft[0] !== undefined || sqft[1] !== undefined) {
      newSearchParams.set("squareFootage", `${sqft[0] || ""},${sqft[1] || ""}`);
    } else {
      newSearchParams.delete("squareFootage");
    }

    if (acreage[0] !== undefined || acreage[1] !== undefined) {
      newSearchParams.set("acreage", `${acreage[0] || ""},${acreage[1] || ""}`);
    } else {
      newSearchParams.delete("acreage");
    }

    if (yearBuilt[0] !== undefined || yearBuilt[1] !== undefined) {
      newSearchParams.set(
        "yearBuilt",
        `${yearBuilt[0] || ""},${yearBuilt[1] || ""}`
      );
    } else {
      newSearchParams.delete("yearBuilt");
    }

    if (isFavorite) {
      newSearchParams.set("favorite", "true");
    } else {
      newSearchParams.delete("favorite");
    }

    if (isShowOnlyPrivatelyOwned) {
      newSearchParams.set("companyOwned", "true");
    } else {
      newSearchParams.delete("companyOwned");
    }

    if (searchByTags) {
      newSearchParams.set("tag", searchByTags);
    } else {
      newSearchParams.delete("tag");
    }

    if (searchByNotes) {
      newSearchParams.set("note", searchByNotes);
    } else {
      newSearchParams.delete("note");
    }

    // Update URL without causing page reload
    const newUrl = `${window.location.pathname}?${newSearchParams.toString()}`;
    try {
      window.history.replaceState({}, "", newUrl);
    } catch (e) {
      // Ignore errors from third-party scripts (e.g., Facebook Pixel) that hook into History API
    }
  }, [
    hasInitialized,
    selectedPropertyTypes,
    beds,
    baths,
    sqft,
    acreage,
    yearBuilt,
    isFavorite,
    isShowOnlyPrivatelyOwned,
    searchByTags,
    searchByNotes,
    searchParams,
  ]);

  const prevUrlRef = useRef<string>();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (selectedIds.length > 0) {
      searchParams.set("selected", selectedIds.join(","));
    } else {
      searchParams.delete("selected");
    }
    const newUrl = `/ns/searches/${activePageTab}?${searchParams.toString()}`;

    if (newUrl !== prevUrlRef.current) {
      prevUrlRef.current = newUrl;
      navigate(newUrl, { replace: true });
    }
  }, [activePageTab, navigate, selectedIds]);

  const isFiltersSidebarOpen = useMemo(
    () => activeSidebar === EnumSidebars.PropertyFilters,
    [activeSidebar]
  );

  const handleBackToListClick = useCallback(() => {
    if (activePageTab === EnumPageTabs.Map) {
      panelStack.reset([EnumSidebars.PropertiesList]);
    } else {
      const remaining = panelStack.stack.filter(
        (panel) => panel !== EnumSidebars.PropertyDetails
      );
      panelStack.reset(remaining);
      if (!remaining.length) {
        toggleSidebar(false);
      }
    }
  }, [panelStack, activePageTab, toggleSidebar]);

  // Opening shows the stack; closing pops the top panel, hiding the sidebar once only one is left
  const handleToggleSidebar = useCallback(
    (isOpen?: boolean) => {
      const updatedIsOpen = isOpen ?? !isSidebarOpen;
      if (updatedIsOpen) {
        if (!panelCount) {
          openPanel(
            activePageTab === EnumPageTabs.Map
              ? EnumSidebars.PropertiesList
              : EnumSidebars.PropertyFilters
          );
        }
        toggleSidebar(true);
        return;
      }
      if (activeSidebar === EnumSidebars.PropertyDetails) {
        handleBackToListClick();
      } else if (panelCount > 1) {
        closeTopPanel();
      } else {
        toggleSidebar(false);
      }
    },
    [
      toggleSidebar,
      isSidebarOpen,
      panelCount,
      activeSidebar,
      activePageTab,
      openPanel,
      closeTopPanel,
      handleBackToListClick,
    ]
  );


  const handlePropertyClick = useCallback(
    (property: Listing | null | undefined) => {
      setSelectedListingId(property?.id ?? null);
      if (property?.geolocation) {
        setMapCenter({
          lat: property.geolocation.lat,
          lng: property.geolocation.lng,
        });
      }
    },
    [setSelectedListingId]
  );

  useEffect(() => {
    const setDefaultSelectedListing = () => {
      // Center map on first property without selecting it
      const shouldCenterOnFirstListing =
        pagedSearchListings?.length &&
        !mapCenter &&
        !hasSetDefaultListingRef.current;

      // Auto-select single listing after each fetch
      const shouldSelectSingleListing =
        pagedSearchListings?.length === 1 && !selectedListingId;

      if (shouldCenterOnFirstListing) {
        const firstListing = pagedSearchListings[0];
        if (firstListing.geolocation) {
          setMapCenter({
            lat: firstListing.geolocation.lat,
            lng: firstListing.geolocation.lng,
          });
        }
        hasSetDefaultListingRef.current = true;
      }

      if (shouldSelectSingleListing) {
        const firstListing = pagedSearchListings[0];
        handlePropertyClick(firstListing);
        if (firstListing.geolocation) {
          setMapCenter({
            lat: firstListing.geolocation.lat,
            lng: firstListing.geolocation.lng,
          });
        }
        openPanel(EnumSidebars.PropertyDetails);
        handleToggleSidebar(true);
      }
    };

    setDefaultSelectedListing();
  }, [
    pagedSearchListings,
    selectedListingId,
    handleToggleSidebar,
    handlePropertyClick,
    mapCenter,
    openPanel,
  ]);

  const handleSelect = (item: ComboBoxListOption) => {
    handleSavedSearchSelect(item);
  };

  const handleActivePageTabChange = useCallback(
    (value: EnumPageTabs) => {
      setActivePageTab(value);

      if (value === EnumPageTabs.Map) {
        if (!isSidebarOpen) {
          panelStack.reset([EnumSidebars.PropertiesList]);
          toggleSidebar(true);
        }
        return;
      }

      const remaining = panelStack.stack.filter(
        (panel) => panel !== EnumSidebars.PropertiesList
      );
      panelStack.reset(remaining);
      if (!remaining.length) {
        toggleSidebar(false);
      }
    },
    [isSidebarOpen, panelStack, toggleSidebar]
  );

  const handleFavoriteClick = useCallback(
    async (listing: Listing) => {
      // TODO: add error handling
      if (listing.isFavorite) {
        await removeFavorite(listing.propertyId as string);
      } else {
        await addFavorite(listing.propertyId as string);
      }
    },
    [addFavorite, removeFavorite]
  );

  const handleRemove = useCallback(
    (item: ComboBoxListOption) => {
      handleSavedSearchRemove(item);
    },
    [handleSavedSearchRemove]
  );

  const defaultSelectedRerunSearch = useMemo(() => {
    const reRunableSearches = savedSearches?.filter(isRegionSearch) || [];
    if (reRunableSearches.length === 1) {
      return reRunableSearches[0];
    }
    return undefined;
  }, [savedSearches]);

  const pageTitle = useMemo(() => {
    if (!savedSearches) return "Neighborhood Search";

    if (savedSearches.length === 1) {
      return savedSearches[0]?.name || "";
    }
    return `${savedSearches?.length} Searches Selected`;
  }, [savedSearches]);

  const handleFiltersClick = useCallback(() => {
    if (!isSidebarOpen) {
      openPanel(EnumSidebars.PropertyFilters);
      toggleSidebar(true);
      return;
    }

    if (isFiltersSidebarOpen) {
      // Reveal the panel underneath, or close the sidebar if there is none
      closePanel(EnumSidebars.PropertyFilters);
      if (panelCount <= 1) {
        toggleSidebar(false);
      }
    } else {
      openPanel(EnumSidebars.PropertyFilters);
    }
  }, [
    isFiltersSidebarOpen,
    isSidebarOpen,
    panelCount,
    openPanel,
    closePanel,
    toggleSidebar,
  ]);

  const handleMapPropertyClick = useCallback(
    (property: Listing | null | undefined) => {
      setSelectedListingId(property?.id);
      openPanel(EnumSidebars.PropertyDetails);
      handleToggleSidebar(true);
    },
    [setSelectedListingId, openPanel, handleToggleSidebar]
  );

  const handleListPropertyClick = useCallback(
    (property: Listing) => {
      setSelectedListingId(property.id);
      if (property.geolocation) {
        setMapCenter({
          lat: property.geolocation.lat,
          lng: property.geolocation.lng,
        });
      }
      openPanel(EnumSidebars.PropertyDetails);
      toggleSidebar(true);
    },
    [setSelectedListingId, openPanel, toggleSidebar]
  );

  const handlePropertyOpenNotesClick = useCallback(
    (listing: Listing) => {
      setManageNotesListing(listing);
    },
    [setManageNotesListing]
  );

  const handlePropertyTagsClick = useCallback(
    (listing: Listing) => {
      setManageTagsListing(listing as Listing);
    },
    [setManageTagsListing]
  );

  // Preserve the last known total records to prevent UI shift during loading
  const displayTotalRecords = useMemo(() => {
    if (
      !pagedSearchListingsIsLoading &&
      pagedSearchListingsTotalRecords !== undefined
    ) {
      lastKnownTotalRecords.current = pagedSearchListingsTotalRecords;
      return pagedSearchListingsTotalRecords;
    }
    return lastKnownTotalRecords.current;
  }, [pagedSearchListingsIsLoading, pagedSearchListingsTotalRecords]);

  const sidebarTitle = useMemo(() => {
    if (activeSidebar === EnumSidebars.PropertyFilters) {
      return "Filters";
    }
    if (
      activeSidebar === EnumSidebars.PropertyDetails &&
      activePageTab === EnumPageTabs.Map
    ) {
      return (
        <button
          onClick={handleBackToListClick}
          className="flex flex-row items-center gap-2 text-sm font-semibold !text-gray-500"
        >
          <span>←</span>
          <span>Back to List</span>
        </button>
      );
    }
    if (activeSidebar === EnumSidebars.PropertiesList) {
      return (
        <span className="text-sm font-semibold !text-gray-500">
          {displayTotalRecords} Properties
        </span>
      );
    }
    return undefined;
  }, [activeSidebar, handleBackToListClick, displayTotalRecords, activePageTab]);

  return (
    <PageLayout
      suppressGutters
      suppressTopPadding
      pinnedHeaderHeight={PAGE_TITLE_HEIGHT}
      pinnedHeader={
        <PageTitle
          titleIcon={<NeighborhoodSearchIcon />}
          title={
            <div className="flex flex-row items-center gap-2">
              <div>{pageTitle}</div>
              <Badge
                variant="secondary"
                className={
                  pagedSearchListingsIsLoading ? "animate-pulse" : ""
                }
              >
                <span
                  className={
                    pagedSearchListingsIsLoading ? "invisible" : "visible"
                  }
                >
                  {displayTotalRecords} properties
                </span>
              </Badge>
              <VerticalDivider className="h-full" />
              <SavedSearchesSelector
                selectedItems={selectedSavedSearchesOptions}
                onSelect={handleSelect}
                onRemove={handleRemove}
              />
              {singleSearchId &&
                statusData?.lookup_status &&
                statusData.lookup_status !==
                  SavedSearchLookupStatusCode.Complete && (
                  <>
                    <VerticalDivider className="h-full" />
                    <LookupStatusIndicator
                      status={getLookupStatusFromCode(
                        statusData.lookup_status as SavedSearchLookupStatusCode
                      )}
                      label={getLookupStatusLabel(statusData.lookup_status)}
                    />
                  </>
                )}
            </div>
          }
          isCondensed
          isUseGutters
          rightSideContent={
            <div className="flex flex-row items-center gap-2">
              <CreditsBalance />
              <VerticalDivider className="h-full" />
              <LinkButton
                variant="default"
                to="/ns/search/start"
                icon={<SearchIcon />}
              >
                New Search
              </LinkButton>
              <LinkButton
                variant="outline"
                to="/ns/search/list"
                icon={<HistoryIcon />}
              >
                Saved Searches
              </LinkButton>
              <VerticalDivider className="h-full" />
              <Tabs
                value={activePageTab}
                onValueChange={(value) => {
                  handleActivePageTabChange(value as EnumPageTabs);
                }}
              >
                <TabsList>
                  <TabsTrigger value={EnumPageTabs.Map}>Map</TabsTrigger>
                  <TabsTrigger value={EnumPageTabs.List}>List</TabsTrigger>
                </TabsList>
              </Tabs>
              <VerticalDivider className="h-full" />
              <ActionsMenu menuItems={neighborhoodMenuItems} />
              <VerticalDivider className="h-full" />
              <Button
                variant={isFiltersSidebarOpen ? "default" : "secondary"}
                icon={<FilterIcon />}
                onClick={handleFiltersClick}
              >
                Filters
              </Button>
            </div>
          }
        />
      }
    >
      <Layout
        isSidebarOpen={isSidebarOpen}
        onSidebarToggleClick={handleToggleSidebar}
        SidebarProps={{
          offsetHeight: 50,
          size: "sm",
          title: sidebarTitle,
        }}
        sidebarContent={
          <Sidebar
            stack={panelStack.stack}
            PropertyFiltersSidebarProps={{
              selectedPropertyTypes,
              onSelectedPropertyTypesChange: setSelectedPropertyTypes,
              beds,
              onBedsChange: setBeds,
              baths,
              onBathsChange: setBaths,
              sqft,
              onSqftChange: setSqft,
              acreage,
              onAcreageChange: setAcreage,
              yearBuilt,
              onYearBuiltChange: setYearBuilt,
              isFavorite,
              onIsFavoriteChange: (value: boolean) => {
                setIsFavorite(value);
                updateSearchParams({ currentPage: 1, favorite: value });
              },
              isShowOnlyPrivatelyOwned,
              onIsShowOnlyPrivatleyOwnedChange: (value: boolean) => {
                setIsShowOnlyPrivatelyOwned(value);
                updateSearchParams({ currentPage: 1, companyOwned: value });
              },
              isHideDNCPhoneNumbers,
              onIsHideDNCPhoneNumbersChange: (value: boolean) => {
                setIsHideDNCPhoneNumbers(value);
                updateSearchParams({
                  currentPage: 1,
                  hideDNCPhoneNumbers: value,
                });
              },
              ownerOccupied,
              onOwnerOccupiedChange: (value: OwnerOccupancyFilter) => {
                setOwnerOccupied(value);
                updateSearchParams({ currentPage: 1, ownerOccupied: value });
              },
              searchByTags,
              onSearchByTagsChange: setSearchByTags,
              searchByNotes,
              onSearchByNotesChange: setSearchByNotes,
            }}
            PropertyDetailsSidebarProps={{
              listing: selectedListing as Listing,
              onAddNoteClick: () =>
                setManageNotesListing(selectedListing as Listing),
              onAddTagClick: () =>
                setManageTagsListing(selectedListing as Listing),
              onFavoriteClick: () =>
                selectedListing && handleFavoriteClick(selectedListing),
              isNotesEnabled: true,
              isTagsEnabled: true,
              isLoading: isLoadingSelectedListing,
              isHideDNCPhoneNumbers: isHideDNCPhoneNumbers,
            }}
            PropertiesListSidebarProps={{
              suppressInlineDetails: true,
              listings: infiniteSearchListings || [],
              selectedListing,
              selectedListingId,
              onListingClick: handleListPropertyClick,
              onListingImageClick: handlePropertyClick,
              onAddNoteClick: () => {
                setManageNotesListing(selectedListing as Listing);
              },
              onAddTagClick: () => {
                setManageTagsListing(selectedListing as Listing);
              },
              onFavoriteClick: handleFavoriteClick,
              isLoadingSelectedListing: isLoadingSelectedListing,
              isLoadingListings: infiniteSearchListingsIsLoading,
              onScrollBottomReached: () => {
                if (infiniteSearchListingsHasNextPage) {
                  infiniteSearchListingsFetchNextPage();
                }
              },
              onAdjustFiltersClick: handleFiltersClick,
              onClearFiltersClick: resetToDefaults,
              isHideDNCPhoneNumbers: isHideDNCPhoneNumbers,
              isFavoritingEnabled: true,
            }}
          />
        }
      >
        <Tabs value={activePageTab} className="w-full">
          <TabsContent
            value={EnumPageTabs.Map}
            forceMount
            className={`${
              activePageTab === EnumPageTabs.Map ? "block" : "hidden"
            } w-full mt-0`}
          >
            {savedSearches && (
              <Map
                mapCenter={mapCenter}
                listings={allSearchListings || []}
                searches={savedSearches}
                selectedListing={selectedListing}
                onListingClick={handleMapPropertyClick}
                isHideDNCPhoneNumbers={isHideDNCPhoneNumbers}
              />
            )}
          </TabsContent>
          <TabsContent
            value={EnumPageTabs.List}
            forceMount
            className={activePageTab === EnumPageTabs.List ? "block" : "hidden"}
          >
            <List
              listings={pagedSearchListings}
              printType={printType}
              selectedListings={selectedListings}
              printContentRef={printContentRef}
              isLoading={pagedSearchListingsIsLoading}
              pageSize={pageSize}
              currentPage={currentPage}
              selectedListing={selectedListing}
              onListingClick={handleListPropertyClick}
              onPropertyOpenNotesClick={handlePropertyOpenNotesClick}
              onPropertyTagsClick={handlePropertyTagsClick}
              onPrintSingleListingClick={handlePrintSingleListingClick}
              onFavoriteClick={handleFavoriteClick}
              isVisible={activePageTab === EnumPageTabs.List}
              toggleItemIsSelected={toggleItemIsSelected}
              onToggleContactIsDisabled={(contact, isDisabled) => {
                // TODO: Implement contact disable functionality
              }}
              isBatchSelectionActive={isBatchSelectionActive}
              totalRecords={pagedSearchListingsTotalRecords || 0}
              onAdjustFiltersClick={handleFiltersClick}
              onClearFiltersClick={() => {
                resetToDefaults();
              }}
              onSearchParamsUpdated={updateSearchParams}
              activeFilters={activeFilters}
              isHideDNCPhoneNumbers={isHideDNCPhoneNumbers}
              selectedItems={selectedItems}
              isSelectAllChecked={isSelectAllChecked}
              toggleSelectAll={toggleSelectAll}
              onListingsTypeClick={setSelectedListingsType}
              onBatchSelectionToggle={toggleIsBatchSelectionActive}
              isFavoritesOnly={isFavorite === true}
              onFavoritesOnlyToggle={() => {
                const newValue = isFavorite === true ? undefined : true;
                setIsFavorite(newValue);
                updateSearchParams({ currentPage: 1, favorite: newValue });
              }}
              sendCardsTemplateCategory="Neighborhood Search"
            />
          </TabsContent>
        </Tabs>
      </Layout>
      {manageNotesListing && (
        <ManageNotes
          onClose={() => setManageNotesListing(null)}
          open
          notes={manageNotesListing.notes || []}
          onDeleteClick={(noteId) => {
            handleDeleteNoteClick({
              listingId: manageNotesListing.propertyId as string,
              noteId,
            });
          }}
          onAddClick={(noteText) => {
            handleAddNoteClick({
              listingId: manageNotesListing.propertyId as string,
              note: noteText,
            });
          }}
        />
      )}
      {isCloneListDialogOpen && (
        <CloneListDialog
          open={isCloneListDialogOpen}
          onClose={() => setIsCloneListDialogOpen(false)}
          initialSelectedSearch={defaultSelectedRerunSearch}
        />
      )}
      {isCombinedListsDialogOpen && (
        <CombineListsDialog
          open={isCombinedListsDialogOpen}
          onClose={() => setIsCombinedListsDialogOpen(false)}
          initialSelectedSearches={savedSearches}
        />
      )}

      {manageTagsListing && (
        <ManageTags
          open={!!manageTagsListing}
          onClose={() => setManageTagsListing(null)}
          tags={manageTagsListing.tags || []}
          onSaveClick={(updatedTags) => {
            handleTagsSaveClick({
              listingId: manageTagsListing.propertyId as string,
              tags: updatedTags,
              previousTags: manageTagsListing.tags || [],
            });
          }}
        />
      )}
      {isPrintExportDialogOpen && (
        <PrintExportDialog
          open
          onOpenChange={handlePrintExportDialogOpenChange}
          outputType={outputType}
          numPrintRecords={actionCount}
          onListingsCsvExportClick={downloadListings}
          onListingsPdfExportClick={exportListingsToPdf}
          onListingsPdfPrintClick={printListings}
          onLabelsPrintClick={() => printAveryLabels("print")}
          onLabelsExportClick={() => printAveryLabels("download")}
          printType={printType}
          onPrintTypeChange={(value) => setPrintType(value as PrintType)}
          isDownloadingListings={isDownloadingListings}
          isSuppressPrintTypeSelection={isPrintingSingleListing}
          isSuppressAveryLabels={isPrintingSingleListing}
          isFetchingListings={isFetchingListingsForPrint}
          exportTemplateOptions={exportTemplateOptions}
          selectedExportTemplate={selectedExportTemplate}
          onExportTemplateChange={setSelectedExportTemplate}
        />
      )}
      <FetchingSendCardInfoDialog
        progress={sendCardsProgress}
        onCancel={cancelSendCards}
        cardCount={actionCount}
      />
      <ConfirmDialog {...partnerSyncDialogProps} />
    </PageLayout>
  );
};
export default SavedSearch;
